export const FRONTDESK_BROWSER_SETUP_KIND = "frontdesk-browser-setup";
export const FRONTDESK_BROWSER_SETUP_VERSION = 1;

/**
 * Values a setup QR carries. The SOL bridge emits the URL only: the SOLTOKEN is never
 * sent to a browser, so staff enter it from inSuite's Sol-Aktivierung page.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const input: BrowserSetupPayloadInput = { insuiteRestUrl: "https://host/sol/x/_rest_" };
 * ```
 */
export interface BrowserSetupPayloadInput {
  readonly insuiteRestUrl: string;
  readonly solToken?: string;
}

export interface BrowserSetupPayloadV1 {
  readonly kind: typeof FRONTDESK_BROWSER_SETUP_KIND;
  readonly version: typeof FRONTDESK_BROWSER_SETUP_VERSION;
  readonly insuiteRestUrl: string;
  readonly solToken?: string;
}

/**
 * Decoded setup QR. `solToken` is empty when the code carried none.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const parsed: ParsedBrowserSetupPayload = {
 *   insuiteRestUrl: "https://host/sol/x/_rest_",
 *   solToken: "",
 *   source: "versioned",
 * };
 * ```
 */
export interface ParsedBrowserSetupPayload {
  readonly insuiteRestUrl: string;
  readonly solToken: string;
  readonly source: "versioned" | "legacy";
}

export type BrowserSetupPayloadParseResult =
  | { readonly ok: true; readonly value: ParsedBrowserSetupPayload }
  | { readonly ok: false; readonly error: string };

export function createBrowserSetupPayload(
  input: BrowserSetupPayloadInput,
): BrowserSetupPayloadV1 {
  const solToken = input.solToken?.trim();
  return solToken === undefined || solToken === ""
    ? {
        kind: FRONTDESK_BROWSER_SETUP_KIND,
        version: FRONTDESK_BROWSER_SETUP_VERSION,
        insuiteRestUrl: input.insuiteRestUrl.trim(),
      }
    : {
        kind: FRONTDESK_BROWSER_SETUP_KIND,
        version: FRONTDESK_BROWSER_SETUP_VERSION,
        insuiteRestUrl: input.insuiteRestUrl.trim(),
        solToken,
      };
}

export function serializeBrowserSetupPayload(
  input: BrowserSetupPayloadInput,
): string {
  return JSON.stringify(createBrowserSetupPayload(input));
}

export function parseBrowserSetupPayload(
  raw: unknown,
): BrowserSetupPayloadParseResult {
  const payload = parseRawPayload(raw);
  if (!payload) {
    return {
      ok: false,
      error: "This QR code does not contain supported FrontDesk browser setup data.",
    };
  }

  if (
    payload["kind"] === FRONTDESK_BROWSER_SETUP_KIND &&
    payload["version"] === FRONTDESK_BROWSER_SETUP_VERSION &&
    typeof payload["insuiteRestUrl"] === "string" &&
    (payload["solToken"] === undefined || typeof payload["solToken"] === "string")
  ) {
    const solToken = payload["solToken"];
    return {
      ok: true,
      value: {
        insuiteRestUrl: payload["insuiteRestUrl"].trim(),
        solToken: typeof solToken === "string" ? solToken.trim() : "",
        source: "versioned",
      },
    };
  }

  if (
    typeof payload["url"] === "string" &&
    typeof payload["token"] === "string"
  ) {
    return {
      ok: true,
      value: {
        insuiteRestUrl: payload["url"].trim(),
        solToken: payload["token"].trim(),
        source: "legacy",
      },
    };
  }

  return {
    ok: false,
    error: "This QR code is not a recognized FrontDesk bridge setup code.",
  };
}

function parseRawPayload(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return isRecord(raw) ? raw : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
